import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardHeader, CardTitle } from '@monigo/ui';
import { supabase } from '../../lib/supabase.js';

const KEYS = [
  'collection_account.EUR',
  'collection_account.USD',
  'collection_account.CAD',
  'support.email',
  'support.faq_url',
] as const;

type Settings = Record<(typeof KEYS)[number], string>;

export function SettingsPage() {
  const { t } = useTranslation();
  const [values, setValues] = useState<Settings>({
    'collection_account.EUR': '',
    'collection_account.USD': '',
    'collection_account.CAD': '',
    'support.email': '',
    'support.faq_url': '',
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from('settings')
        .select('key,value_jsonb')
        .in('key', [...KEYS]);
      const next: Partial<Settings> = {};
      for (const row of data ?? []) {
        next[row.key as keyof Settings] = JSON.stringify(row.value_jsonb, null, 2);
      }
      setValues((v) => ({ ...v, ...next }));
    })();
  }, []);

  async function save() {
    setSaving(true);
    for (const k of KEYS) {
      const raw = values[k];
      let parsed: unknown;
      try {
        parsed = raw ? JSON.parse(raw) : null;
      } catch {
        parsed = raw;
      }
      await supabase
        .from('settings')
        .upsert({ key: k, value_jsonb: parsed }, { onConflict: 'key' });
    }
    setSaving(false);
    setSavedAt(Date.now());
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.collectionAccountsTitle')}</CardTitle>
      </CardHeader>
      <div className="flex flex-col gap-3">
        {KEYS.map((k) => {
          const labelKey =
            k === 'collection_account.EUR'
              ? 'settings.collectionEur'
              : k === 'collection_account.USD'
                ? 'settings.collectionUsd'
                : k === 'collection_account.CAD'
                  ? 'settings.collectionCad'
                  : k === 'support.email'
                    ? 'settings.supportEmail'
                    : k === 'support.faq_url'
                      ? 'settings.faqUrl'
                      : k;
          return (
            <label key={k} className="flex flex-col gap-1 text-sm">
              <span className="text-text-muted text-xs uppercase tracking-wide">{t(labelKey)}</span>
              <textarea
                className="min-h-24 rounded-md border border-border bg-surface p-2 font-mono text-xs"
                value={values[k]}
                onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
              />
            </label>
          );
        })}
        <Button onClick={save} isLoading={saving}>
          {t('common.save')}
        </Button>
        {savedAt ? <p className="text-sm text-success">✓</p> : null}
      </div>
    </Card>
  );
}
